import React, { useState } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Select,
  Grid,
  GridItem,
  Card,
  CardHeader,
  CardBody,
  Icon,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Progress,
  useColorModeValue,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  Divider,
  Input,
  FormControl,
  FormLabel
} from '@chakra-ui/react';
import {
  FaFileAlt,
  FaDownload,
  FaChartBar,
  FaCalendarAlt,
  FaUsers,
  FaBriefcase,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaFileExcel,
  FaFilePdf
} from 'react-icons/fa';
import api from '../../../../services/api';

const ReportCard = ({ title, description, icon, color, onGenerate, loading }) => {
  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue(`${color}.200`, `${color}.600`);

  return (
    <Card bg={bgColor} borderLeft="4px" borderColor={borderColor}>
      <CardHeader pb={2}>
        <HStack justify="space-between">
          <HStack>
            <Icon as={icon} boxSize={5} color={`${color}.500`} />
            <Text fontWeight="bold">{title}</Text>
          </HStack>
          <Badge colorScheme={color}>Relatório</Badge>
        </HStack>
      </CardHeader>
      <CardBody>
        <Text fontSize="sm" color="gray.600" mb={4}>
          {description}
        </Text>
        <Button
          colorScheme={color}
          size="sm"
          leftIcon={<FaDownload />}
          onClick={onGenerate}
          isLoading={loading}
          loadingText="Gerando..."
          width="full"
        >
          Gerar Relatório
        </Button>
      </CardBody>
    </Card>
  );
};

const DepartmentReports = () => {
  const [loadingReport, setLoadingReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [selectedFormat, setSelectedFormat] = useState('pdf');
  const toast = useToast();

  const generateReport = async (type) => {
    try {
      setLoadingReport(type);
      
      const params = {
        type,
        period: selectedPeriod,
        format: selectedFormat
      };
      
      if (selectedPeriod === 'custom') {
        params.startDate = customStartDate;
        params.endDate = customEndDate;
      }
      
      const response = await api.post('/juridico/diretoria/report', params);
      const data = response.data || response;
      
      if (data.downloadUrl) {
        // Download the report
        window.open(data.downloadUrl, '_blank');
        toast({
          title: 'Relatório gerado',
          description: `Relatório ${type} foi gerado com sucesso.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        // Display report data in UI
        setReportData(data);
        toast({
          title: 'Relatório gerado',
          description: 'Os dados do relatório estão disponíveis abaixo.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Error generating report:', err);
      toast({
        title: 'Erro ao gerar relatório',
        description: err.response?.data?.message || 'Erro ao gerar o relatório',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoadingReport(null);
    }
  };

  const reports = [
    {
      title: 'Relatório de Produtividade',
      description: 'Análise detalhada da produtividade da equipe jurídica por período',
      icon: FaChartBar,
      color: 'blue',
      type: 'productivity'
    },
    {
      title: 'Relatório de Precatórios',
      description: 'Status completo de todos os precatórios processados',
      icon: FaBriefcase,
      color: 'green',
      type: 'precatorios'
    },
    {
      title: 'Relatório de Equipe',
      description: 'Performance individual de cada membro da equipe',
      icon: FaUsers,
      color: 'purple',
      type: 'team'
    },
    {
      title: 'Relatório de Análises',
      description: 'Detalhamento das análises documentais realizadas',
      icon: FaCheckCircle,
      color: 'teal',
      type: 'analysis'
    },
    {
      title: 'Relatório Executivo',
      description: 'Resumo executivo do departamento jurídico',
      icon: FaFileAlt,
      color: 'orange',
      type: 'executive'
    },
    {
      title: 'Relatório de Pendências',
      description: 'Lista de precatórios pendentes e em atraso',
      icon: FaClock,
      color: 'red',
      type: 'pending'
    }
  ];

  return (
    <Box>
      <VStack spacing={6} align="stretch">
        <Box>
          <HStack spacing={3} mb={2}>
            <Icon as={FaFileAlt} boxSize={6} color="orange.600" />
            <Heading size="lg">Relatórios do Departamento</Heading>
          </HStack>
          <Text color="gray.600">
            Gere relatórios detalhados sobre o departamento jurídico
          </Text>
        </Box>

        <Box bg="white" p={6} rounded="lg" shadow="sm">
          <Heading size="md" mb={4}>Configurações do Relatório</Heading>
          
          <Grid templateColumns="repeat(3, 1fr)" gap={4}>
            <GridItem>
              <FormControl>
                <FormLabel>Período</FormLabel>
                <Select value={selectedPeriod} onChange={(e) => setSelectedPeriod(e.target.value)}>
                  <option value="week">Última Semana</option>
                  <option value="month">Último Mês</option>
                  <option value="quarter">Último Trimestre</option>
                  <option value="year">Último Ano</option>
                  <option value="custom">Período Personalizado</option>
                </Select>
              </FormControl>
            </GridItem>
            
            {selectedPeriod === 'custom' && (
              <>
                <GridItem>
                  <FormControl>
                    <FormLabel>Data Início</FormLabel>
                    <Input 
                      type="date" 
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>Data Fim</FormLabel>
                    <Input 
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                    />
                  </FormControl>
                </GridItem>
              </>
            )}
            
            <GridItem>
              <FormControl>
                <FormLabel>Formato</FormLabel>
                <Select value={selectedFormat} onChange={(e) => setSelectedFormat(e.target.value)}>
                  <option value="pdf">PDF</option>
                  <option value="excel">Excel</option>
                  <option value="csv">CSV</option>
                </Select>
              </FormControl>
            </GridItem>
          </Grid>
        </Box>

        <Grid templateColumns="repeat(3, 1fr)" gap={4}>
          {reports.map((report) => (
            <GridItem key={report.type}>
              <ReportCard
                title={report.title}
                description={report.description}
                icon={report.icon}
                color={report.color}
                onGenerate={() => generateReport(report.type)}
                loading={loadingReport === report.type}
              />
            </GridItem>
          ))}
        </Grid>

        {reportData && (
          <>
            <Divider />
            <Box bg="white" p={6} rounded="lg" shadow="sm">
              <Heading size="md" mb={4}>Dados do Relatório</Heading>
              
              {reportData.summary && (
                <Grid templateColumns="repeat(4, 1fr)" gap={4} mb={6}>
                  <GridItem>
                    <Stat>
                      <StatLabel>Total Processados</StatLabel>
                      <StatNumber>{reportData.summary.totalProcessed || 0}</StatNumber>
                    </Stat>
                  </GridItem>
                  <GridItem>
                    <Stat>
                      <StatLabel>Aprovados</StatLabel>
                      <StatNumber color="green.500">
                        {reportData.summary.approved || 0}
                      </StatNumber>
                    </Stat>
                  </GridItem>
                  <GridItem>
                    <Stat>
                      <StatLabel>Reprovados</StatLabel>
                      <StatNumber color="red.500">
                        {reportData.summary.rejected || 0}
                      </StatNumber>
                    </Stat>
                  </GridItem>
                  <GridItem>
                    <Stat>
                      <StatLabel>Em Análise</StatLabel>
                      <StatNumber color="orange.500">
                        {reportData.summary.inProgress || 0}
                      </StatNumber>
                    </Stat>
                  </GridItem>
                </Grid>
              )}
              
              {reportData.details && reportData.details.length > 0 && (
                <TableContainer>
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        {Object.keys(reportData.details[0]).map((key) => (
                          <Th key={key}>{key}</Th>
                        ))}
                      </Tr>
                    </Thead>
                    <Tbody>
                      {reportData.details.map((row, index) => (
                        <Tr key={index}>
                          {Object.values(row).map((value, idx) => (
                            <Td key={idx}>{value}</Td>
                          ))}
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </>
        )}
      </VStack>
    </Box>
  );
};

export default DepartmentReports;